import { Menu, MenuItem } from '@mui/material';
import {
  type KeyboardEvent,
  type Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { showTitleOnlyWhenTruncated } from '@vestigas/utils';

import { Icon, IconButton, SearchInput } from '@vestigas/ui';

import { type FilterBadge } from './savedFiltersBadgeRowUtils';

type SavedFilterOverflowMenuProps = {
  readonly badges: readonly FilterBadge[];
  readonly buttonAriaLabel: string;
  readonly noResultsLabel: string;
  readonly searchPlaceholder: string;
  readonly wrapperRef?: Ref<HTMLDivElement>;
  readonly onSelect: (badge: FilterBadge) => void;
};

/**
 * The 3-dot overflow button shown when saved-filter tabs do not all fit into
 * the badge row, opening a searchable menu of the hidden filters.
 *
 * Follows the FiltersDrawerAddFieldMenu pattern (IconButton + MUI Menu).
 */
export function SavedFilterOverflowMenu({
  badges,
  buttonAriaLabel,
  noResultsLabel,
  onSelect,
  searchPlaceholder,
  wrapperRef,
}: SavedFilterOverflowMenuProps) {
  const menuId = useId();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const isOpen = Boolean(anchorElement);

  const normalizedTerm = searchTerm.trim().toLowerCase();
  const matchingBadges = badges.filter((badge) =>
    badge.label.toLowerCase().includes(normalizedTerm),
  );

  function handleClose() {
    setAnchorElement(null);
    setSearchTerm('');
  }

  // autoFocus fires in the same commit that mounts the menu, racing MUI's
  // popover transition and focus trap. The next frame lands after both.
  useEffect(() => {
    if (!isOpen) return;

    const frameId = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className="inline-flex h-9 shrink-0 items-center">
      <IconButton
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen ? 'true' : undefined}
        aria-haspopup="menu"
        aria-label={buttonAriaLabel}
        className="text-tiga-text-default"
        size="sm"
        type="button"
        onClick={(event) => {
          setAnchorElement(event.currentTarget);
        }}
      >
        <Icon metaphor="moreVert" className="size-4.5" />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        autoFocus={false}
        id={menuId}
        open={isOpen}
        slotProps={{
          paper: {
            className: 'mt-1 min-w-64 max-w-80 rounded-md shadow-lg',
          },
        }}
        onClose={handleClose}
      >
        <div
          className="px-3 py-1"
          onKeyDownCapture={(event: KeyboardEvent<HTMLDivElement>) => {
            // Arrows must reach the MenuList so keyboard users can move from
            // the search input down into the filter items.
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') return;

            // SearchInput handles Escape itself and stops propagation, so the
            // Menu would never see it: with text it clears, when empty it just
            // blurs to <body> and the menu becomes uncloseable by keyboard.
            // Intercept the empty case here and close.
            if (event.key === 'Escape') {
              if (searchTerm === '') {
                event.stopPropagation();
                handleClose();
              }
              return;
            }

            // Keep printable keys in the input; MUI Menu would otherwise use
            // them for menu-item type-ahead and steal focus.
            event.stopPropagation();
          }}
        >
          <SearchInput
            ref={searchInputRef}
            debounceTime={0}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={setSearchTerm}
          />
        </div>
        {matchingBadges.length === 0 ? (
          <MenuItem disabled className="text-sm">
            {noResultsLabel}
          </MenuItem>
        ) : (
          matchingBadges.map((badge) => (
            <MenuItem
              key={badge.id}
              className="block min-w-0 truncate text-sm"
              onClick={() => {
                onSelect(badge);
                handleClose();
              }}
              onMouseEnter={(event) => {
                showTitleOnlyWhenTruncated(event, badge.label);
              }}
            >
              {badge.label}
            </MenuItem>
          ))
        )}
      </Menu>
    </div>
  );
}
